import { api } from './api';
import type { ApiResponse, User, AuthTokens } from '../types';

export interface SignupRequest {
  email: string;
  password: string;
  name?: string;
}

export interface SigninRequest {
  email: string;
  password: string;
}

export interface ConfirmRequest {
  email: string;
  code: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  email: string;
  code: string;
  new_password: string;
}

export interface SigninResponse {
  user: User;
  tokens: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_in: number;
  };
}

export interface MfaChallengeResponse {
  challenge: {
    challenge_name: 'SOFTWARE_TOKEN_MFA';
    session: string;
    email: string;
  };
}

export interface MfaVerifyRequest {
  session: string;
  code: string;
  email: string;
}

export interface MfaSetupResponse {
  secret_code: string;
  otpauth_uri: string;
}

export interface MfaStatusResponse {
  is_mfa_enabled: boolean;
}

export const authService = {
  // 新規登録
  signup: async (data: SignupRequest): Promise<ApiResponse<{ user_id: string; email: string; status: string }>> => {
    const response = await api.post('/auth/signup', data);
    return response.data;
  },

  // メール認証確認
  confirm: async (data: ConfirmRequest): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/confirm', data);
    return response.data;
  },

  // 確認メール再送信
  resendConfirmation: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/resend-confirmation', { email });
    return response.data;
  },

  // ログイン
  signin: async (data: SigninRequest): Promise<ApiResponse<SigninResponse>> => {
    const response = await api.post('/auth/signin', data);
    return response.data;
  },

  // ログアウト
  logout: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // トークンリフレッシュ
  refresh: async (refreshToken: string): Promise<ApiResponse<AuthTokens>> => {
    const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  },

  // パスワードリセット要求
  requestPasswordReset: async (email: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/password/reset', { email });
    return response.data;
  },

  // パスワードリセット確認
  confirmPasswordReset: async (data: PasswordResetConfirmRequest): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.post('/auth/password/confirm', data);
    return response.data;
  },

  // Googleログイン
  googleSignin: async (idToken: string): Promise<ApiResponse<SigninResponse>> => {
    const response = await api.post('/auth/google', { id_token: idToken });
    return response.data;
  },

  // プロフィール取得
  getProfile: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/users/me');
    return response.data;
  },

  // プロフィール更新
  updateProfile: async (data: { name?: string; email_notifications?: boolean; weekly_report_enabled?: boolean; notification_prefs?: Record<string, boolean> }): Promise<ApiResponse<User>> => {
    const response = await api.put('/users/me', data);
    return response.data;
  },

  // パスワード変更
  changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.put('/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // メールアドレス変更リクエスト（パスワード確認 + 確認コード送信）
  changeEmail: async (password: string, newEmail: string): Promise<ApiResponse<{ status: string }>> => {
    const response = await api.post('/users/me/email', { password, new_email: newEmail });
    return response.data;
  },

  // メールアドレス変更確認（コード検証）
  verifyEmail: async (code: string): Promise<ApiResponse<{ user: User }>> => {
    const response = await api.post('/users/me/email/verify', { code });
    return response.data;
  },

  // 退会
  deleteAccount: async (password: string, reason_category?: string, reason_detail?: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.delete('/users/me', { data: { password, reason_category, reason_detail } });
    return response.data;
  },

  // MFA verify（チャレンジ応答）
  mfaVerify: async (data: MfaVerifyRequest): Promise<ApiResponse<SigninResponse>> => {
    const response = await api.post('/auth/mfa-verify', data);
    return response.data;
  },

  // MFAセットアップ
  mfaSetup: async (): Promise<ApiResponse<MfaSetupResponse>> => {
    const response = await api.post('/auth/mfa-setup');
    return response.data;
  },

  // MFA確認（有効化）
  mfaConfirm: async (code: string): Promise<ApiResponse<{ is_mfa_enabled: boolean }>> => {
    const response = await api.post('/auth/mfa-confirm', { code });
    return response.data;
  },

  // MFA状態取得
  mfaStatus: async (): Promise<ApiResponse<MfaStatusResponse>> => {
    const response = await api.get('/auth/mfa-status');
    return response.data;
  },
};
